import useModal from '@/hooks/use-modals';
import { Dialog, DialogActions, DialogContent, Icon, IconButton, styled } from '@mui/material';
import { ReactNode, useEffect, useState, useCallback } from 'react';
import useAnalytics, { eventName } from '@/hooks/use-analytics';

interface ModalDialogProps {
  id: string;
  children: ReactNode;
  footerChildren?: ReactNode;
  status: 'open' | 'closed';
  sticky_footer?: boolean;
}

export function ModalDialog({ id, children, footerChildren, status, sticky_footer }: ModalDialogProps) {
  const [open, setOpen] = useState(status === 'open');
  const { setModal, deleteModal } = useModal();
  const { trackEvent } = useAnalytics();

  const handleOpen = useCallback(() => {
    trackEvent(`${eventName(id)}_MODAL_OPENED`);
    setOpen(true);
  }, [id, trackEvent]);

  const handleClose = useCallback(() => {
    trackEvent(`${eventName(id)}_MODAL_CLOSED`);
    setOpen(false);
  }, [id, trackEvent]);

  const handleToggle = useCallback(() => {
    trackEvent(`${eventName(id)}_MODAL_TOGGLED_${open ? 'CLOSED' : 'OPEN'}`);
    setOpen((prev) => !prev);
  }, [id, open, trackEvent]);

  useEffect(() => {
    setModal(id, { open: handleOpen, close: handleClose, toggle: handleToggle });

    return () => deleteModal(id);
  }, [deleteModal, handleClose, handleOpen, handleToggle, id, setModal]);

  return (
    <>
      <StyledDialog
        onClose={handleClose}
        aria-label={id.split('_').join(' ')}
        open={open}
        data-open={open.toString()}
      >
        <StyledIconButton
          aria-label='close'
          onClick={handleClose}
        >
          <Icon>close</Icon>
        </StyledIconButton>
        {sticky_footer !== false ? (
          <>
            <StyledDialogContent>{children}</StyledDialogContent>
            {footerChildren && (
              <DialogFooter
                sticky
                disableSpacing
              >
                {footerChildren}
              </DialogFooter>
            )}
          </>
        ) : (
          <StyledDialogContent>
            {children}
            {footerChildren && (
              <DialogFooter
                sticky={false}
                disableSpacing
              >
                {footerChildren}
              </DialogFooter>
            )}
          </StyledDialogContent>
        )}
      </StyledDialog>
    </>
  );
}

const scrimHeight = 45;

const baseScrimStyes = `
  content: '';
  position: absolute;
  left: 0;
  width: 100%;
  height: ${scrimHeight}px;
  pointer-events: none;
`;

const StyledDialog = styled(Dialog)`
  & .MuiModal-backdrop {
    background-color: var(--mui-palette-background-backdrop);
    backdrop-filter: blur(4px);
  }

  & .MuiDialog-paper {
    width: min(var(--scene-max-width), 100%);
    border-radius: 1rem;
    background-image: var(--mui-palette-background-paper);
    opacity: 0;
    transform: scale(0.96);
    transition:
      opacity 400ms var(--ease-out-quart),
      transform 400ms var(--ease-out-quart),
      overlay 400ms var(--ease-out-quart) allow-discrete,
      display 400ms var(--ease-out-quart) allow-discrete;

    &::before {
      ${baseScrimStyes}
      top: 0;
      background-image: linear-gradient(to bottom, var(--mui-palette-background-paper), transparent);
    }
    &::after {
      ${baseScrimStyes}
      bottom: 0;
      background-image: linear-gradient(to top, var(--mui-palette-background-paper), transparent);
    }
  }

  &[data-open='true'] .MuiDialog-paper {
    opacity: 1;
    transform: scale(1);
  }

  @starting-style {
    &[data-open='true'] .MuiDialog-paper {
      opacity: 0;
      transform: scale(0.96);
    }
  }

  @media (max-width: 600px) {
    & .MuiDialog-container {
      align-items: flex-end;
    }

    & .MuiDialog-paper {
      border-radius: 26px 26px 0 0;
      margin: 0;
      transform: translateY(100%);
    }

    &[data-open='true'] .MuiDialog-paper {
      transform: translateY(0);
    }

    @starting-style {
      &[data-open='true'] .MuiDialog-paper {
        transform: translateY(100%);
      }
    }
  }
`;

const StyledIconButton = styled(IconButton)`
  position: absolute;
  right: 8px;
  top: 8px;
  color: var(--mui-palette-text-secondary);
  z-index: 100;
`;

const StyledDialogContent = styled(DialogContent)`
  padding: 30px;
  padding-right: 28px;
  margin-right: 2px; /* handles scenarios with modal scrollbar */
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  gap: 10px;

  &::-webkit-scrollbar {
    width: 4px;
    background-color: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: var(--mui-palette-border-main);
    border-radius: 4px;
  }
`;

const DialogFooter = styled(DialogActions, {
  shouldForwardProp: (prop) => prop !== 'sticky',
})<{ sticky?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 30px;
  padding-top: 10px;

  ${({ sticky }) =>
    sticky
      ? `
    position: relative;
    z-index: 1; /* bumps the footer up above the scrim */
    &::before {
      ${baseScrimStyes}
      top: -${scrimHeight}px;
      background-image: linear-gradient(to top, var(--mui-palette-background-paper), transparent);
    }
  `
      : `
    position: static;
    /* FIX WIDTH: undo dialog content padding */
    margin: 0 -30px;
    padding-left: 30px;
    padding-right: 30px;

    &::before {
      display: none;
    }
  `}
`;
